import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  DATA_SOURCES,
  DataSourcesType,
  DataToggleLabels,
  OutputFormatting,
  SITREP_SECTION_STEPS,
  SitrepSectionStep,
  SitrepSectionWizardType,
} from './types';
import { toast } from 'react-toastify';
import { useCreateSitrepSectionAndTemplate } from './useCreateSitrepSectionAndTemplate';
import { DEFAULT_SEERIST_REQUEST } from '../../DisasterGPT/SeeristRequestBuilder';

// This context stores state related to each of the tabs in the Sitrep Section Wizard
interface SitrepSectionWizardContextType {
  // Current step of the wizard
  activeStep: SitrepSectionStep;
  handleNext: () => void;
  handleBack: () => void;
  // Section step
  sectionName: string;
  setSectionName: React.Dispatch<React.SetStateAction<string>>;
  sectionType: SitrepSectionWizardType;
  setSectionType: React.Dispatch<React.SetStateAction<SitrepSectionWizardType>>;
  aiPrompt: string;
  setAiPrompt: React.Dispatch<React.SetStateAction<string>>;
  // Data step
  selectedDataToggleLabel: DataToggleLabels;
  setSelectedDataToggleLabel: React.Dispatch<
    React.SetStateAction<DataToggleLabels>
  >;
  selectedDataSources: DataSourcesType[];
  handleSetSelectedDataSources: (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
    source: DataSourcesType
  ) => void;
  uploadedFiles: File[];
  handleSetUploadedFiles: (files: FileList | null) => void;
  // Finalize step
  outputFormatting: OutputFormatting[];
  handleSetOutputFormatting: (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
    newOutputFormatting: OutputFormatting
  ) => void;
  handleEditButtonClick: (step: SitrepSectionStep) => void;
  handleCreateSectionTemplate: () => void;
}

const SitrepSectionWizardContext = createContext<
  SitrepSectionWizardContextType | undefined
>(undefined);

export const useSitrepSectionWizardContext = () => {
  const context = useContext(SitrepSectionWizardContext);
  if (!context) {
    throw new Error(
      'useSitrepSectionWizardContext must be used within a SitrepSectionWizardProvider'
    );
  }
  return context;
};

interface SitrepSectionWizardProviderProps {
  setIsEditingSitrepSections: (isEditing: boolean) => void;
  children: React.ReactNode;
}
export const SitrepSectionWizardProvider = ({
  setIsEditingSitrepSections,
  children,
}: SitrepSectionWizardProviderProps) => {
  const [activeStep, setActiveStep] = useState<SitrepSectionStep>(
    SITREP_SECTION_STEPS[0]
  );
  const [sectionName, setSectionName] = useState<string>('');
  const [sectionType, setSectionType] = useState<SitrepSectionWizardType>('ai');
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [selectedDataToggleLabel, setSelectedDataToggleLabel] =
    useState<DataToggleLabels>(DATA_SOURCES);
  const [selectedDataSources, setSelectedDataSources] = useState<
    DataSourcesType[]
  >([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [outputFormatting, setOutputFormatting] = useState<OutputFormatting[]>(
    []
  );

  const { mutate } = useCreateSitrepSectionAndTemplate();

  // Runs when the user clicks on the next button
  const handleNext = useCallback(() => {
    // Automated / manual sections are created from step 1 — skip AI-only steps.
    if (
      activeStep.id === 1 &&
      (sectionType === 'automated' || sectionType === 'manual')
    ) {
      return;
    }
    const nextStepIndex =
      SITREP_SECTION_STEPS.findIndex((step) => step.id === activeStep.id) + 1;
    if (nextStepIndex < SITREP_SECTION_STEPS.length) {
      setActiveStep(SITREP_SECTION_STEPS[nextStepIndex]);
    }
  }, [activeStep.id, sectionType]);

  // Runs when the user clicks on the back button
  const handleBack = useCallback(() => {
    const prevStepIndex =
      SITREP_SECTION_STEPS.findIndex((step) => step.id === activeStep.id) - 1;
    if (prevStepIndex >= 0) {
      setActiveStep(SITREP_SECTION_STEPS[prevStepIndex]);
    }
  }, [activeStep.id]);

  // Handler for selecting data sources
  const handleSetSelectedDataSources = useCallback(
    (
      e: React.MouseEvent<HTMLInputElement, MouseEvent>,
      source: DataSourcesType
    ) => {
      if (e.currentTarget.checked) {
        setSelectedDataSources((prev) => [...prev, source]);
      } else {
        setSelectedDataSources((prev) =>
          prev.filter((dataSource) => dataSource !== source)
        );
      }
    },
    []
  );

  // Handler for setting uploaded files
  const handleSetUploadedFiles = useCallback((files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setUploadedFiles((prev) => {
        if (prev.length + fileArray.length > 10) {
          toast.info('Maximum 10 files can be uploaded');
          return prev;
        }
        return [...prev, ...fileArray];
      });
    }
  }, []);

  // Handler for setting output formatting
  const handleSetOutputFormatting = useCallback(
    (
      e: React.MouseEvent<HTMLInputElement, MouseEvent>,
      newOutputFormatting: OutputFormatting
    ) => {
      if (e.currentTarget.checked) {
        setOutputFormatting((prev) => [...prev, newOutputFormatting]);
      } else {
        setOutputFormatting((prev) =>
          prev.filter((outputFormat) => outputFormat !== newOutputFormatting)
        );
      }
    },
    []
  );

  // Moves user to the step they are choosing to edit
  const handleEditButtonClick = useCallback((step: SitrepSectionStep) => {
    setActiveStep(step);
  }, []);

  const handleCreateSectionTemplate = () => {
    if (sectionType === 'automated') {
      // Seed a default request; users configure filters in the saved section.
      const automation = {
        provider: 'seerist' as const,
        seeristRequest: DEFAULT_SEERIST_REQUEST,
      };
      mutate({
        name: sectionName || 'Seerist',
        type: 'automated',
        data_sources: [],
        ai_instructions: '',
        automation,
        output_format_structured: {
          summary_options: [],
          automation,
        },
      });
    } else if (sectionType === 'manual') {
      mutate({
        name: sectionName,
        type: 'manual',
        data_sources: [],
        ai_instructions: '',
        output_format_structured: { summary_options: [] },
      });
    } else {
      mutate({
        name: sectionName,
        type: 'ai',
        ai_instructions: aiPrompt,
        data_sources: selectedDataSources,
        output_format_structured: { summary_options: outputFormatting },
      });
    }
    setIsEditingSitrepSections(false);
  };

  return (
    <SitrepSectionWizardContext.Provider
      value={{
        activeStep,
        sectionName,
        setSectionName,
        sectionType,
        setSectionType,
        aiPrompt,
        setAiPrompt,
        selectedDataToggleLabel,
        setSelectedDataToggleLabel,
        selectedDataSources,
        uploadedFiles,
        handleSetUploadedFiles,
        outputFormatting,
        handleSetOutputFormatting,
        handleNext,
        handleBack,
        handleSetSelectedDataSources,
        handleEditButtonClick,
        handleCreateSectionTemplate,
      }}
    >
      {children}
    </SitrepSectionWizardContext.Provider>
  );
};
